// WB-M1A (doc 23 §13.4.7) -- raw HTTP client of the Borna-owned whiteboard.
//
// Two same-origin, cookie-session calls to Subway. Nothing here reaches the
// tutor upstream: the board is loaded and saved from Borna's own database,
// independently of the chat (WB-M1-D13).
//
// Transport only: errors are rethrown in the `{status, body}` shape the rest
// of the AI Tutor client uses; the store module decides what they mean.

import config from '@/config'
import { httpGet, httpPut, axiosInstance } from '../NetworkService'

// Browsers cap the total body size of keepalive requests (64 KiB in
// Chromium). A page-hide save is attempted only below this; above it the
// request would be refused outright. It is best-effort either way and never
// part of the correctness model (doc 23 §13.4.9).
export const BOARD_KEEPALIVE_MAX_BYTES = 60 * 1024

// P40 QA-28: a save that gets no answer at all (a connection that is up
// but goes nowhere) ends as a transient failure -- the retry ladder and the
// NOT SAVED state take over -- instead of holding a close or a route leave
// indefinitely. Generous: a full 3 MiB board on a slow uplink fits.
export const BOARD_SAVE_TIMEOUT_MS = 45000

function boardUrl(conversationId) {
  return `${config.serverRoot}/api/ai-tutor/conversations/${encodeURIComponent(
    conversationId
  )}/board`
}

function unwrap(call) {
  return call
    .then(response => response.data)
    .catch(err => {
      if (err && err.response) {
        throw { status: err.response.status, body: err.response.data }
      }
      throw { status: 0, body: err && err.message }
    })
}

/**
 * Returns the conversation's board, creating it on first open.
 * @param {string} conversationId -- the Borna conversation (Reports) id
 * @returns {Promise<{boardId: string, conversationId: string, version: number,
 *   scene: object, sceneBytes: number, elementCount: number, limits: object}>}
 */
export function getBoard(conversationId) {
  return unwrap(httpGet(boardUrl(conversationId)))
}

/**
 * Saves the scene if, and only if, the board is still at `expectedVersion`.
 * A 409 with code AI_TUTOR_BOARD_VERSION_CONFLICT means another save landed
 * first; nothing was written.
 * @param {string} conversationId
 * @param {{expectedVersion: number, scene: object}} payload
 * @returns {Promise<{boardId: string, version: number, updatedAt: string}>}
 */
export function saveBoard(conversationId, payload) {
  return unwrap(
    httpPut(boardUrl(conversationId), payload, {
      timeout: BOARD_SAVE_TIMEOUT_MS,
    })
  )
}

/**
 * Best-effort save while the page is being hidden or unloaded. Returns
 * whether a request was handed to the browser at all; its outcome is never
 * known. NOT a persistence guarantee.
 * @param {string} conversationId
 * @param {{expectedVersion: number, scene: object}} payload
 * @returns {boolean}
 */
export function saveBoardKeepalive(conversationId, payload) {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return false
  }
  let body
  try {
    body = JSON.stringify(payload)
  } catch (err) {
    return false
  }
  if (body.length > BOARD_KEEPALIVE_MAX_BYTES) return false
  const headers = { 'Content-Type': 'application/json' }
  const defaults = axiosInstance.defaults.headers || {}
  const csrfToken = defaults.put && defaults.put['X-CSRF-TOKEN']
  if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken
  try {
    window
      .fetch(boardUrl(conversationId), {
        method: 'PUT',
        keepalive: true,
        credentials: 'include',
        headers,
        body,
      })
      .catch(() => undefined)
    return true
  } catch (err) {
    return false
  }
}
